'use client';

import SvgChevronright from '@common/__generated__/icon/SvgChevronright';
import { Button, Chip } from '@common/components';
import { SvgTabMoreLeft, SvgTabMoreRight } from '@common/icon';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useState } from 'react';

import { UnauthQueryOptions } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { GiftListItem } from '@/app/_components/DonationTemp/GiftListItem';
import { GIFT_SALE_STATUS } from '@/app/donation/constants';
import { Responsive } from '@/components/organism/Responsive';
import { BREAKPOINTS, DONATION_ROUTES, STORAGE_KEY } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { GIFT_PRODUCT_SORT_TYPE, SORT_TYPE_OPTIONS } from './constants';
import { DesktopSpecialtiesItem } from './SpecialtiesItem';
import type { GiftProductSortType } from './types';

interface RegionalGiftsSectionProps {
  className?: string;
}

export const RegionalGiftsSection = ({
  className,
}: RegionalGiftsSectionProps) => {
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  const [selectedInstitutionId, setSelectedInstitutionId] = useState<number>();
  const [selectedSortType, setSelectedSortType] = useState<GiftProductSortType>(
    GIFT_PRODUCT_SORT_TYPE.LATEST,
  );

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
  });

  const PAGE_SIZE = isDesktop ? 20 : 6;

  useEffect(() => {
    try {
      const savedInstitutionId = sessionStorage.getItem(
        STORAGE_KEY.DONATION_HOME_SELECTED_SPECIALTY_INSTITUTION_ID,
      );
      if (!savedInstitutionId || Number.isNaN(Number(savedInstitutionId)))
        return;

      setSelectedInstitutionId(Number(savedInstitutionId));
    } catch {
      // sessionStorage 접근 불가 (private browsing 등)
    }
  }, []);

  const { data: specialties = [] } = useQuery({
    ...UnauthQueryOptions[
      'get:/wello/v2/allows/home-love/main/institution-specialty'
    ](),
    select: (data) => data.context?.contents ?? [],
  });

  const { data: giftList, isLoading: isLoadingGiftList } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/home/gift-product']({
      query: {
        institutionId: isDesktop ? selectedInstitutionId : undefined,
        page: 1,
        size: PAGE_SIZE,
        giftProductSortType:
          isDesktop && selectedInstitutionId ? selectedSortType : undefined,
      },
    }),
    select: (data) => data.context?.contents ?? [],
    enabled: !!selectedInstitutionId,
  });

  const { data: popularGiftList, isLoading: isLoadingPopularGiftList } =
    useQuery({
      ...UnauthQueryOptions['get:/wello/v2/allows/home/gift-product/popular']({
        query: {
          size: 20,
        },
      }),
      select: (data) => data.context?.contents ?? [],
      enabled: !selectedInstitutionId,
    });

  const institutionList = [
    {
      institutionId: undefined,
      institutionName: '전체',
      newDisplayYN: false,
      specialtyImageUrl: SELECTED_INSTITUTION_DEFAULT_IMAGE_URL,
      specialtyName: '전체',
    },
    ...specialties.map(
      ({
        institutionId: institution_id,
        institutionName: institution_name,
        newDisplayYN: new_display_yn,
        specialtyImageUrl: specialty_image_url,
        specialtyName: specialty_name,
      }) => ({
        institutionId: institution_id,
        institutionName: institution_name,
        newDisplayYN: new_display_yn,
        specialtyImageUrl: specialty_image_url,
        specialtyName: specialty_name,
      }),
    ),
  ];

  const handleClickInstitution = (institutionId?: number) => {
    setSelectedInstitutionId(institutionId);
    setSelectedSortType(GIFT_PRODUCT_SORT_TYPE.LATEST);

    try {
      if (!institutionId) {
        sessionStorage.removeItem(
          STORAGE_KEY.DONATION_HOME_SELECTED_SPECIALTY_INSTITUTION_ID,
        );

        return;
      }

      sessionStorage.setItem(
        STORAGE_KEY.DONATION_HOME_SELECTED_SPECIALTY_INSTITUTION_ID,
        institutionId.toString(),
      );
    } catch {
      // sessionStorage 접근 불가 (private browsing 등)
    }
  };

  const isSortFilterShow = Boolean(selectedInstitutionId);
  const resultGiftList = selectedInstitutionId ? giftList : popularGiftList;
  const isLoading = isLoadingGiftList || isLoadingPopularGiftList;

  return (
    <section
      className={cn(
        'flex flex-col items-center px-40 content-container',
        'mobile-tablet:px-20',
        className,
      )}
    >
      <header className="flex w-full flex-col items-center">
        <h2
          className={cn(
            'pb-32 text-center font-800 text-Gray-900 typo-h4',
            'mobile-tablet:w-full mobile-tablet:pb-14 mobile-tablet:text-left mobile-tablet:font-700 mobile-tablet:typo-body1',
          )}
        >
          🎁 엄선된 상위 1% 인기 답례품
        </h2>
        {/* 모바일 에서는 안보이는 선택 지역 버튼 */}
        <Responsive.DesktopOnly
          display="flex"
          className="w-full justify-center"
        >
          <Button
            theme="sub-text"
            className="ml-26 mr-12 mt-[1.97rem] size-fit p-0"
            onClick={() => emblaApi?.scrollPrev()}
          >
            <SvgTabMoreLeft className="size-24" originalFill />
          </Button>
          <div
            className="w-fit max-w-full overflow-hidden pb-32"
            ref={emblaRef}
          >
            <div className="flex gap-12">
              {institutionList.map(
                (
                  {
                    institutionId,
                    institutionName,
                    specialtyName,
                    specialtyImageUrl,
                    newDisplayYN,
                  },
                  index,
                ) => (
                  <div key={index} className="flex-[0_0_auto]">
                    <DesktopSpecialtiesItem
                      imageUrl={specialtyImageUrl}
                      specialtyName={specialtyName}
                      institutionName={institutionName}
                      isNew={newDisplayYN}
                      active={selectedInstitutionId === institutionId}
                      onClick={() => handleClickInstitution(institutionId)}
                    />
                  </div>
                ),
              )}
            </div>
          </div>
          <Button
            theme="sub-text"
            className="ml-12 mr-26 mt-[1.97rem] size-fit p-0"
            onClick={() => emblaApi?.scrollNext()}
          >
            <SvgTabMoreRight className="size-24" originalFill />
          </Button>
        </Responsive.DesktopOnly>

        {isSortFilterShow && (
          <ul
            className={cn(
              'flex items-center gap-6 pb-24',
              'mobile-tablet:hidden',
            )}
          >
            {SORT_TYPE_OPTIONS.map(({ label, value }) => (
              <li key={value}>
                <Chip
                  checked={selectedSortType === value}
                  onClick={() => setSelectedSortType(value)}
                >
                  {label}
                </Chip>
              </li>
            ))}
          </ul>
        )}
      </header>
      <ul
        className={cn(
          'grid w-full grid-cols-4 gap-x-20 gap-y-40 pb-60',
          'mobile:grid-cols-2 tablet:grid-cols-3 mobile-tablet:gap-x-15 mobile-tablet:gap-y-32 mobile-tablet:pb-40',
        )}
      >
        {isLoading
          ? Array.from({ length: PAGE_SIZE }).map((_, index) => (
              <li key={index}>
                <GiftListItem isLoading />
              </li>
            ))
          : resultGiftList?.map((giftData) => (
              <li key={giftData.productNumber}>
                <GiftListItem
                  mainImageSizes={`${BREAKPOINTS.MOBILE} 50vw, ${BREAKPOINTS.TABLET} 33vw, 285px`}
                  responsiveClassName={GIFT_ITEM_RESPONSIVE_CLASSNAME}
                  mainImageUrl={giftData.mainImageUrl}
                  productName={giftData.productName}
                  donationAmount={giftData.requiredDonationAmount}
                  reviewCount={giftData.reviewCount}
                  reviewRating={giftData.reviewRating}
                  institution={{
                    id: giftData.institutionId,
                    logoUrl: giftData.institutionLogoUrl,
                    name: giftData.institutionName,
                  }}
                  productNumber={giftData.productNumber}
                  isNew={giftData.newDisplayYN}
                  isAdult={giftData.adultYN}
                  isSoldOut={
                    giftData.productSaleStatus === GIFT_SALE_STATUS.SOLD_OUT
                  }
                />
              </li>
            ))}
      </ul>
      <Button theme="sub-line" dimensions={isDesktop ? 'H56' : 'H40'} asChild>
        <CustomLink
          href={DONATION_ROUTES.GIFTS.pathname}
          aria-label="전체 답례품 목록 페이지로 이동"
          className="flex w-[30rem] max-w-full items-center gap-16"
        >
          답례품 전체보기
          <SvgChevronright className="size-16 text-Gray-600" />
        </CustomLink>
      </Button>
    </section>
  );
};

const SELECTED_INSTITUTION_DEFAULT_IMAGE_URL =
  'https://cdn.welfarehello.com/static/donation-quick-menu-all-pc.png';

// GiftListItem 공통 props
const GIFT_ITEM_RESPONSIVE_CLASSNAME = {
  institutionLogo: 'mobile-tablet:size-20',
  institutionName: 'mobile-tablet:typo-caption1 mobile-tablet:font-600',
  productName: 'mobile-tablet:typo-body4 mobile-tablet:font-500',
  donationAmount: 'mobile-tablet:typo-body3 mobile-tablet:font-700',
  donationAmountText: 'mobile-tablet:typo-body4 mobile-tablet:font-600',
} as const;
